import {
  LogOut,
  Settings,
  User,
  Store,
  Menu,
  Search,
  Bell,
  UserCircle,
  X,
  Check,
  ChevronDown,
  Crown,
} from "lucide-react"
import { useState, useRef, useEffect } from "react"
import StoreSelect from "./StoreSelect"
import { useAuth } from "../../context/AuthContext"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { allStoresData, userData } from "../../store/actions"
import { BASE_URL } from "../../api/api"
import SearchResultModal from "./SearchResultModal"
import GoalFitGymSetupModal from "./GoalFitGymSetupModal"
import SubscriptionModal from "../subscrptions/SubscriptionModal"
import { runSubscriptionUpgrade } from "../../utils/subscriptionUpgrade"
import { getCurrentPlan } from "../../utils/permissions"
import { getStoreExperience } from "../../utils/storeExperience"
import { resolveStoreCategory } from "../../utils/storeCategory"

type Props = {
  onMenuClick: () => void
}

export default function Topbar({ onMenuClick }: Props) {
  const userdata = useSelector((state: any) => state.UserReducer.userdata)
  const { user, store } = userdata

  const userPlan = getCurrentPlan(userdata)
  const availablePlans = userdata?.availablePlans
  const currentBillingCycle = userdata?.subscription?.billingCycle || userdata?.user?.subscriptionBillingCycle || "monthly"
  const experience = getStoreExperience(store)
  const isGymStore = resolveStoreCategory(store) === "GYM"
  const goalFitLive = Boolean(store?.goalfitEnabled || store?.goalfitProfile?.enabled)

  const [query, setQuery] = useState("")
  const [results, setResults] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedResult, setSelectedResult] = useState<any | null>(null)
  const [openResultModal, setOpenResultModal] = useState(false)
  const [openSubscription, setOpenSubscription] = useState(false)
  const [openGoalFitSetup, setOpenGoalFitSetup] = useState(false)
  const [showLoggingOut, setShowLoggingOut] = useState(false)

  const isActive = query.length > 0

  const [openUserMenu, setOpenUserMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const dispatch = useDispatch()
  const { logout } = useAuth()
  const navigate = useNavigate()

  /* Close user menu on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenUserMenu(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  useEffect(() => {
    dispatch(userData())
    dispatch(allStoresData())
  }, [dispatch])

  /* 🔍 GLOBAL SEARCH */
  useEffect(() => {
    if (!query || query.length < 2 || !store?._id) {
      setResults([])
      return
    }

    const fetchSearch = async () => {
      try {
        setLoading(true)
        const res = await fetch(
          `${BASE_URL}/api/gestock/stores/${store._id}/search?q=${query}`
        )
        const data = await res.json()
        setResults(data)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }

    const debounce = setTimeout(fetchSearch, 300)
    return () => clearTimeout(debounce)
  }, [query, store?._id])

  return (
    <>
      <header className="
        sticky top-0 z-30
        backdrop-blur-xl
        px-4 sm:px-6 py-3
      ">
        <div className={`absolute inset-0 -z-10 border-b ${experience.topbar.headerClass}`} />
        {/* ================= TOP ROW ================= */}
        <div className="flex items-center justify-between gap-3">

          {/* LEFT */}
          <div className="flex items-center gap-3">
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-xl hover:bg-gray-100 transition"
            >
              <Menu size={22} />
            </button>

            <StoreSelect store={store} />
            <span className={`hidden xl:inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${experience.topbar.badgeClass}`}>
              {experience.badge}
            </span>
          </div>

          {/* RIGHT */}
          <div className="flex items-center gap-2 sm:gap-3">
            {isGymStore && (
              <button
                onClick={() => setOpenGoalFitSetup(true)}
                className={`inline-flex items-center gap-2 rounded-xl border px-2.5 py-2 transition ${
                  goalFitLive
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
                title="Configurer la diffusion GoalFit"
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-md border ${
                    goalFitLive
                      ? "border-emerald-500 bg-emerald-500 text-white"
                      : "border-slate-300 bg-white text-transparent"
                  }`}
                >
                  <Check size={12} />
                </span>
                <span className="hidden xl:inline text-sm font-semibold">GoalFit</span>
              </button>
            )}

            <button className="relative p-2 rounded-xl hover:bg-gray-100 transition">
              <Bell size={20} />
              <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
            </button>

            {/* USER MENU */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setOpenUserMenu(v => !v)}
                className="
                  flex items-center gap-2 px-2 sm:px-3 py-1.5
                  rounded-xl bg-gray-100 hover:bg-gray-200
                  transition
                "
              >
                <UserCircle size={26} className="text-gray-500" />

                <div className="hidden sm:block text-left">
                  <p className="text-sm font-semibold text-gray-700">
                    {user?.fullName || "Administrateur"}
                  </p>
                  <p className="text-xs text-gray-400">{user?.role === "ADMIN" ? "Administrateur" : "Vendeur"}</p>
                  {/* <p className="text-xs text-gray-400">
                    Plan : {userPlan}
                  </p> */}
                </div>

                {/* 🔽🔼 ARROW */}
                <ChevronDown
                  size={18}
                  className={`
                    text-gray-400 transition-transform duration-300
                    ${openUserMenu ? "rotate-180" : ""}
                  `}
                />
              </button>

              {openUserMenu && (
                <div className="
                  absolute right-0 mt-3 w-64
                  bg-white rounded-2xl shadow-xl border
                  overflow-hidden z-50
                ">
                  <div className="px-4 py-3 border-b">
                    <p className="font-semibold text-gray-800">
                      {user?.fullName || "Administrateur"}
                    </p>
                    <p className="text-xs text-gray-500">{user?.phone}</p>
                    <p className="text-xs text-gray-400">
                      Plan : {userPlan}
                    </p>
                  </div>

                  <div className="py-2">
                    <MenuItem
                      icon={<User size={16} />}
                      label="Mon profil"
                      onClick={() => {
                        setOpenUserMenu(false)
                        navigate("/profile")
                      }}
                    />

                    <MenuItem
                      icon={<Settings size={16} />}
                      label="Paramètres"
                      onClick={() => {
                        setOpenUserMenu(false)
                        navigate("/settings")
                      }}
                    />

                    {/* ⭐ SUBSCRIPTION ACCESS */}
                    <MenuItem
                      icon={<Crown size={16} className="text-yellow-500" />}
                      label="Abonnement"
                      onClick={() => {
                        setOpenUserMenu(false)
                        setOpenSubscription(true)
                      }}
                    />

                    <MenuItem
                      icon={<Store size={16} />}
                      label="Mes boutiques"
                      onClick={() => {
                        setOpenUserMenu(false)
                        navigate("/stores")
                      }}
                    />
                  </div>

                  <div className="border-t">
                    <MenuItem
                      icon={<LogOut size={16} />}
                      label="Déconnexion"
                      danger
                      onClick={() => {
                        setShowLoggingOut(true)
                        setTimeout(() => {
                          setShowLoggingOut(false);
                          // navigate("/dashboard")
                          logout()
                          navigate("/login")
                        }, 1800)


                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ================= SEARCH ================= */}
        <div className="mt-3 flex justify-center">
          <div className="relative w-full max-w-2xl">

            <div
              className={`
                flex items-center gap-3 px-4 py-2
                rounded-2xl border bg-white shadow-sm
                transition-all duration-300
                ${isActive
                  ? experience.topbar.searchRingClass
                  : "border-gray-200"}
              `}
            >
              <div className={`
                h-10 w-10 rounded-xl flex items-center justify-center
                ${isActive ? experience.topbar.searchActiveClass : experience.topbar.searchIdleClass}
              `}>
                <Search size={18} />
              </div>

              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={
                  experience.searchPlaceholder
                }
                className="
                  flex-1 bg-transparent outline-none border-none
                  ring-0 focus:ring-0 focus:outline-none
                  text-sm sm:text-base text-gray-800
                  placeholder:text-gray-400
                "
              />

              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* RESULTS (unchanged) */}
            {(results.length > 0 || (!loading && query.length >= 2)) && (
              <div className="
                absolute mt-2 w-full
                bg-white rounded-2xl shadow-xl border
                max-h-80 overflow-y-auto
                divide-y z-50
              ">
                {results.length > 0 ? (
                  results.map((r) => (
                    <button
                      key={r._id}
                      className="w-full px-4 py-3 text-left hover:bg-blue-50 transition"
                      onClick={() => {
                        setSelectedResult(r)
                        setOpenResultModal(true)
                        setQuery("")
                        setResults([])
                      }}
                    >
                      <p className="text-sm font-semibold text-gray-800">
                        {r.modelId?.name} • {r.imei}
                      </p>
                      <p className="text-xs text-gray-500">
                        {r.clientName
                          ? `Client: ${r.clientName} (${r.clientPhone})`
                          : "En stock"}
                      </p>
                      {r.remaining > 0 && (
                        <p className="text-xs text-red-600 font-semibold">
                          Reste : {r.remaining.toLocaleString()} FCFA
                        </p>
                      )}
                    </button>
                  ))
                ) : (
                  <div className="px-6 py-10 text-center text-gray-500">
                    <Search size={28} className="mx-auto mb-3 text-gray-300" />
                    <p className="font-medium text-gray-600">
                      Aucun résultat
                    </p>
                    <p className="text-sm mt-1">
                      {experience.kind === "general"
                        ? "Essayez avec un produit, un client ou une vente"
                        : experience.kind === "car"
                          ? "Essayez avec un vehicule, une reference ou un client"
                          : experience.kind === "service"
                            ? "Essayez avec un service, un devis ou un client"
                          : "Essayez avec un bien, un quartier ou un client"}
                    </p>
                  </div>
                )}
              </div>
            )}

            {loading && (
              <p className="text-xs text-gray-400 mt-2 text-center">
                Recherche…
              </p>
            )}

          </div>
        </div>
      </header>

      <SearchResultModal
        isOpen={openResultModal}
        data={selectedResult}
        onClose={() => {
          setOpenResultModal(false)
          setSelectedResult(null)
        }}
      />

      <GoalFitGymSetupModal
        isOpen={openGoalFitSetup}
        store={store}
        onClose={() => setOpenGoalFitSetup(false)}
        onSaved={() => {
          dispatch(userData())
          setOpenGoalFitSetup(false)
        }}
      />

      {/* ⭐ SUBSCRIPTION MODAL */}
      <SubscriptionModal
        isOpen={openSubscription}
        currentPlan={userPlan}
        currentBillingCycle={currentBillingCycle}
        plans={availablePlans}
        onClose={() => setOpenSubscription(false)}
        onUpgrade={(planKey, billingCycle) =>
          runSubscriptionUpgrade({
            planKey,
            billingCycle,
            currentPlan: userPlan,
            currentBillingCycle,
            user,
            dispatch,
            onClose: () => setOpenSubscription(false),
          })
        }
      />

      {showLoggingOut && <LoggingOutModal />}
    </>
  )
}

/* ================= MENU ITEM ================= */

function MenuItem({
  icon,
  label,
  onClick,
  danger = false,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 px-4 py-2.5 text-sm transition
        ${danger ? "text-red-600 hover:bg-red-50" : "text-gray-700 hover:bg-gray-50"}
      `}
    >
      {icon}
      <span className="font-medium">{label}</span>
    </button>
  )
}

function LoggingOutModal() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-3xl lg:ml-52 shadow-xl px-10 py-8 flex flex-col items-center gap-4 animate-fadeIn">

        {/* Loader */}
        <div className="h-12 w-12 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />

        {/* Text */}
        <h3 className="text-lg font-semibold text-gray-800">
          Déconnexion en cours
        </h3>

        <p className="text-sm text-gray-500 text-center max-w-xs">
          Nous sécurisons votre session et vous déconnectons en toute sécurité…
        </p>

      </div>
    </div>
  )
}
